import React, { useCallback, useMemo, useState, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  NodeTypes,
  EdgeTypes,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  getStraightPath,
  useReactFlow,
  useNodesInitialized,
  NodeDragHandler,
  XYPosition,
  ReactFlowProvider,
} from 'reactflow';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceX,
  forceY,
  SimulationNodeDatum,
} from 'd3-force';
import 'reactflow/dist/style.css';

interface Characteristic {
  answer: string;
  explanation: string;
  confidence: number | null;
  importance: number | null;
}

interface Document {
  id: string;
  title: string;
  content: string;
  url: string;
}

interface Documentation {
  document: Document;
  characteristic_data: Record<string, string[]>;
  summary: string;
  name: string;
}

interface DocumentGraphProps {
  documentation: Documentation[];
}

interface SimulationNode extends Node, SimulationNodeDatum {
  fx?: number;
  fy?: number;
}

interface LayoutControls {
  toggle: () => void;
  isRunning: () => boolean;
}

type LayoutedElementsReturn = [boolean, LayoutControls, {
  start: NodeDragHandler;
  drag: NodeDragHandler;
  stop: () => void;
}];

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: Record<string, string[]>[];
  columns: string[];
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, data, columns }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[90vw] max-h-[80vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td
                      key={`${index}-${column}`}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {row[column].join(', ')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const nodeWidth = 300;
const nodeHeight = 80;

// Create force simulation with stronger forces for better separation
const simulation = forceSimulation<SimulationNode>()
  .force('charge', forceManyBody().strength(-2000))
  .force('x', forceX().x(0).strength(0.1))
  .force('y', forceY().y(0).strength(0.1))
  .alphaTarget(0.05)
  .stop();

const useLayoutedElements = (): LayoutedElementsReturn => {
  const { getNodes, setNodes, getEdges, fitView } = useReactFlow();
  const initialized = useNodesInitialized();
  const draggingNodeRef = useRef<Node | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const simulationRef = useRef(simulation);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const dragEvents = useMemo(
    () => ({
      start: ((_event, node) => (draggingNodeRef.current = node)) as NodeDragHandler,
      drag: ((_event, node) => (draggingNodeRef.current = node)) as NodeDragHandler,
      stop: () => (draggingNodeRef.current = null),
    }),
    [],
  );

  return useMemo(() => {
    let nodes = getNodes().map((node) => {
      // Set initial positions based on node type
      let x = 0;
      if (node.type === 'characteristic') {
        x = 0; // Left side
      } else if (node.type === 'answer') {
        x = 600; // Middle
      } else if (node.type === 'document') {
        x = 1200; // Right side
      }
      
      return {
        ...node,
        x: x,
        y: node.position.y,
      };
    }) as SimulationNode[];
    
    let edges = getEdges();
    let running = true; // Start running by default

    if (!initialized || nodes.length === 0) {
      return [false, { toggle: () => {}, isRunning: () => false }, dragEvents];
    }

    // Update simulation nodes and edges without resetting
    simulationRef.current.nodes(nodes);
    simulationRef.current.force(
      'link',
      forceLink<SimulationNode, Edge>(edges)
        .id((d) => d.id)
        .strength(2.5)
        .distance(600),
    );

    const tick = () => {
      if (!nodes || nodes.length === 0) return;

      getNodes().forEach((node, i) => {
        if (i >= nodes.length) return; // Guard against array bounds
        
        const dragging = draggingNodeRef.current?.id === node.id;

        if (dragging && draggingNodeRef.current) {
          nodes[i].fx = draggingNodeRef.current.position.x;
          nodes[i].fy = draggingNodeRef.current.position.y;
        } else {
          delete nodes[i].fx;
          delete nodes[i].fy;
        }
      });

      simulationRef.current.tick();
      setNodes(
        nodes.map((node) => ({
          ...node,
          position: { 
            x: node.fx ?? node.x ?? 0, 
            y: node.fy ?? node.y ?? 0 
          },
        })),
      );

      if (running) {
        window.requestAnimationFrame(tick);
      }
    };

    // Start the simulation
    window.requestAnimationFrame(tick);

    const toggle = () => {
      running = !running;
      if (running) {
        window.requestAnimationFrame(tick);
      } else {
        simulationRef.current.stop();
      }
    };

    const isRunning = () => running;

    return [true, { toggle, isRunning }, dragEvents];
  }, [initialized, dragEvents, getNodes, getEdges, setNodes, fitView]);
};

const DocumentGraphInner: React.FC<DocumentGraphProps> = ({ documentation }) => {
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    title: string;
    data: Array<{ [key: string]: string[] }>;
    columns: string[];
  }>({
    isOpen: false,
    title: '',
    data: [],
    columns: [],
  });

  const [initialized, { toggle, isRunning }, dragEvents] = useLayoutedElements();

  // Create unique nodes for characteristics and answers
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeIds = new Set<string>();
    const edgeIds = new Set<string>();

    // First, filter out documents that don't have any characteristics
    const documentsWithCharacteristics = documentation.filter(doc => 
      Object.keys(doc.characteristic_data).length > 0
    );

    // Create nodes and edges
    documentsWithCharacteristics.forEach((doc, docIndex) => {
      const docNodeId = `doc-${doc.document.id}`;
      if (!nodeIds.has(docNodeId)) {
        nodes.push({
          id: docNodeId,
          type: 'document',
          position: { x: 1200, y: 0 },
          data: { label: doc.name },
          sourcePosition: Position.Left,
          targetPosition: Position.Right,
        });
        nodeIds.add(docNodeId);
      }

      Object.entries(doc.characteristic_data).forEach(([key, value]) => {
        const charNodeId = `char-${key}`;

        if (!nodeIds.has(charNodeId)) {
          nodes.push({
            id: charNodeId,
            type: 'characteristic',
            position: { x: 0, y: 0 },
            data: { label: key },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
          });
          nodeIds.add(charNodeId);
        }

        const edgeId = `${charNodeId}-${docNodeId}`;
        if (!edgeIds.has(edgeId)) {
          edges.push({
            id: edgeId,
            source: charNodeId,
            target: docNodeId,
            type: 'straight',
            focusable: true,
          });
          edgeIds.add(edgeId);
        }
      });
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [documentation]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'characteristic') {
      // Show table of documents and their answers for this characteristic
      const characteristicData = documentation
        .filter(doc => doc.characteristic_data[node.data.label])
        .map(doc => ({
          Document: [doc.name],
          Answer: doc.characteristic_data[node.data.label],
        }));

      setModalData({
        isOpen: true,
        title: `Answers for Characteristic: ${node.data.label}`,
        data: characteristicData,
        columns: ['Document', 'Answer'],
      });
    } else if (node.type === 'document') {
      // Show table of characteristics and their answers for this document
      const doc = documentation.find(d => d.name === node.data.label);
      if (doc) {
        const documentData = Object.entries(doc.characteristic_data).map(([char, answer]) => ({
          Characteristic: [char],
          Answer: answer,
        }));

        setModalData({
          isOpen: true,
          title: `Characteristics for Document: ${node.data.label}`,
          data: documentData,
          columns: ['Characteristic', 'Answer'],
        });
      }
    }
  }, [documentation]);

  const handleCloseModal = useCallback(() => {
    setModalData(prev => ({ ...prev, isOpen: false }));
  }, []);

  const nodeTypes = useMemo<NodeTypes>(() => ({
    document: ({ data }) => (
      <div className="px-6 py-3 shadow-md rounded-md bg-white border-2 border-blue-500 min-w-[300px] max-w-[400px]">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center">
          <div className="text-sm font-bold">{data.label}</div>
        </div>
      </div>
    ),
    characteristic: ({ data }) => (
      <div className="px-6 py-3 shadow-md rounded-md bg-white border-2 border-green-500 min-w-[250px] max-w-[350px]">
        <Handle type="source" position={Position.Right} />
        <div className="flex items-center">
          <div className="text-sm font-bold">{data.label}</div>
        </div>
      </div>
    ),
  }), []);

  const edgeTypes = useMemo<EdgeTypes>(() => ({
    straight: (props) => {
      const [edgePath] = getStraightPath({
        sourceX: props.sourceX,
        sourceY: props.sourceY,
        targetX: props.targetX,
        targetY: props.targetY,
      });

      return (
        <path
          id={props.id}
          className="react-flow__edge-path"
          d={edgePath}
          style={{
            stroke: '#b1b1b7',
            strokeWidth: 1,
          }}
        />
      );
    },
  }), []);

  return (
    <div style={{ width: '100%', height: '90vh', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onEdgesChange={onEdgesChange}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={handleNodeClick}
        onNodeDragStart={dragEvents.start}
        onNodeDrag={dragEvents.drag}
        onNodeDragStop={dragEvents.stop}
        fitView
        fitViewOptions={{ 
          padding: 0.2,
          minZoom: 0.1,
          maxZoom: 4,
          includeHiddenNodes: false
        }}
        minZoom={0.1}
        maxZoom={4}
        defaultViewport={{ x: 0, y: 0, zoom: 0 }}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        panOnScroll={true}
        panOnDrag={true}
        zoomActivationKeyCode="Control"
        draggable={true}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        style={{ width: '100%', height: '100%' }}
      >
        <Background />
        <Controls 
          showInteractive={true}
          position="bottom-right"
          style={{ bottom: 20, right: 20 }}
        />
      </ReactFlow>
      <Modal
        isOpen={modalData.isOpen}
        onClose={handleCloseModal}
        title={modalData.title}
        data={modalData.data}
        columns={modalData.columns}
      />
    </div>
  );
};

const DocumentGraph: React.FC<DocumentGraphProps> = (props) => {
  return (
    <ReactFlowProvider>
      <DocumentGraphInner {...props} />
    </ReactFlowProvider>
  );
};

export default DocumentGraph; 